import type { ClockAlarm } from "./sync";
import type { VerifyReason } from "./verify";

/**
 * The refusals the screen layer produces on its own, which verification knows
 * nothing about.
 *
 * `verifyPresentation` answers a question about bytes and never throws; these
 * two are the phone failing underneath it — IndexedDB rejecting a write, or a
 * camera that would not open. They are not `VerifyReason` cases because
 * `verify.ts` does not return them, and a reason the verifier can never produce
 * has no business in the enum its `switch` is checked against.
 */
export type ScreenReason = "device-error" | "camera-unavailable";

/** Anything a verifier can be refused with, from either layer. */
export type RefusalReason = VerifyReason | ScreenReason;

/** Verification returns stable English identifiers, and every refusal a
 * verifier can be shown is worded here, so no verification path ever builds a
 * user-facing string. Only the refusal copy: the screens under `web/src/screens/`
 * write their own Spanish markup in place, and always did. */
export const REASON_COPY: Record<RefusalReason, string> = {
  "ok": "Identidad verificada",
  "malformed": "Este código no es una identidad de la UH",
  "clock-unsynced": "Este teléfono nunca ha sincronizado la hora. Conéctate una vez antes de verificar.",
  "unknown-issuer-key": "Firmada con una llave de la UH que esta aplicación no conoce",
  "bad-issuer-signature": "La firma de la UH no es válida",
  "credential-revoked": "La UH revocó esta credencial",
  "credential-expired": "La credencial está vencida",
  "presentation-stale": "Este código tiene más de un minuto. Pide que lo generen de nuevo.",
  "presentation-future": "La hora de este código no cuadra con la de este teléfono",
  "bad-device-signature": "Este código no lo generó el teléfono dueño de la credencial",
  "nonce-replayed": "Este código ya se verificó. Pide uno nuevo.",
  "unsupported-mode": "Este código usa un modo que esta aplicación no conoce",
  "challenge-unknown": "Este código no responde a ningún desafío pendiente de este teléfono: ya se usó, pasaron los dos minutos o el desafío era de otro. Muestra un desafío nuevo.",
  "errand-malformed": "El recado que trae este código no es un recado de la UH",
  "errand-unknown-issuer-key": "La identidad de quien autoriza el recado está firmada con una llave de la UH que esta aplicación no conoce",
  "errand-bad-issuer-signature": "La firma de la UH en la identidad de quien autoriza el recado no es válida",
  "errand-grantor-revoked": "La UH revocó la identidad de quien autorizó este recado",
  "errand-grantor-expired": "La identidad de quien autorizó este recado está vencida",
  "errand-bad-signature": "Este recado no lo firmó el teléfono de quien dice autorizarlo",
  "errand-wrong-bearer": "Este recado autoriza a otra persona, no a quien lo presenta",
  "errand-expired": "Este recado está vencido",
  "device-error": "No se pudo completar la verificación en este teléfono. Inténtalo otra vez.",
  "camera-unavailable": "No se pudo abrir la cámara. Dale permiso a la aplicación y vuelve a intentarlo.",
};

/** The two clock alarms, shown on the card and the scan screen. A warning and
 *  not a block: the phone still generates and verifies, and the person holding
 *  it decides whether to trust it. */
export const ALARM_COPY: Record<ClockAlarm, string> = {
  "sync-stale":
    "Este teléfono lleva más de una semana sin conectarse a la UH. La hora y la lista de credenciales revocadas pueden estar atrasadas: conéctate a la red.",
  "clock-moved":
    "El reloj de este teléfono se atrasó desde la última vez que se conectó a la UH. Conéctate a la red para volver a ponerlo en hora.",
};

/** The alarms as markup, empty when there are none. */
export function alarmsHTML(alarms: ClockAlarm[]): string {
  return alarms.map((alarm) => `<p class="warn alarm">${escapeHTML(ALARM_COPY[alarm])}</p>`).join("");
}

/**
 * Whether this document is the installed application rather than a browser tab.
 *
 * Safari deletes script-writable storage, IndexedDB included, after seven days
 * of browser use with no interaction with the site, and exempts web apps added
 * to the home screen. A credential kept in a tab can therefore vanish on its
 * own and take the device key with it, so the spec makes installation a
 * requirement and not a comfort: `el alta no se da por terminada hasta que la
 * aplicación está instalada`.
 *
 * Two legs because no single one answers everywhere. `display-mode` is the
 * standard query and is what Chrome and Firefox report. Safari on iOS answers
 * it too from 16.4 onward, but `navigator.standalone` is the older signal it
 * has always set for a home-screen web app, and an iPhone a year behind is the
 * exact device this has to be right on.
 */
export function isInstalled(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

/**
 * A sheet or an overlay follows a finger dragging it down, and closes when let
 * go far enough down; short of that it springs back. The grab handle drawn on
 * every sheet promised this and nothing did it.
 *
 * Only from the top of its scroll: a panel scrolled into a long list of
 * errands scrolls back up first, the way every phone sheet behaves. Touch
 * events rather than pointer events, because Chrome cancels a pointer stream
 * the moment it decides the gesture is a scroll, and this gesture starts out
 * looking exactly like one.
 */
export function swipeToClose(panel: HTMLElement, close: () => void): void {
  const CLOSE_AT = 90;
  let startY: number | null = null;
  let dy = 0;
  const settle = (to: string) => {
    panel.style.transition = "transform 0.18s ease-out";
    panel.style.transform = to;
  };
  panel.addEventListener("touchstart", (event) => {
    startY = panel.scrollTop <= 0 && event.touches.length === 1 ? event.touches[0].clientY : null;
    dy = 0;
    panel.style.transition = "none";
  }, { passive: true });
  panel.addEventListener("touchmove", (event) => {
    if (startY === null) return;
    dy = Math.max(0, event.touches[0].clientY - startY);
    panel.style.transform = dy > 0 ? `translateY(${dy}px)` : "";
  }, { passive: true });
  const end = () => {
    if (startY === null) return;
    startY = null;
    if (dy > CLOSE_AT) {
      settle("translateY(100%)");
      // The layer is hidden by the history move `close` makes, which happens
      // after this frame; the transform is cleared once it is out of sight.
      setTimeout(() => (panel.style.transform = ""), 200);
      close();
    } else {
      settle("");
    }
  };
  panel.addEventListener("touchend", end);
  panel.addEventListener("touchcancel", end);
}

/**
 * A moment the person reading has to act on, such as when an errand expires,
 * in their phone's time zone: «30 sept, 14:05». Day and time and no year,
 * because nothing this app dates is more than a week away.
 */
export function formatWhen(seconds: number): string {
  return new Date(seconds * 1000).toLocaleString("es", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

const ENTITIES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};

/**
 * Every credential attribute that reaches `innerHTML` goes through here.
 *
 * The attributes on the result screen arrive from a stranger's phone. They are
 * issuer-signed by the time they are drawn, so the only way to get a `<script>`
 * onto a verifier's screen is to get one signed — which is a directory record
 * away once VS2 binds `UserDirectory` to the real LDAP and a display name
 * stops being something this repo writes. Escaping here costs four characters
 * and closes it before that lands.
 *
 * Nullish becomes the empty string rather than the word `undefined`, which is
 * what interpolating a missing attribute would otherwise print on a card.
 */
export function escapeHTML(value: unknown): string {
  return value == null ? "" : String(value).replace(/[&<>"]/g, (char) => ENTITIES[char]);
}
